import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextInput } from "flowbite-react";
import { BiCheckCircle, BiSearch } from "react-icons/bi";
import { AiFillStar, AiOutlineStar } from "react-icons/ai";
import { CustomButton } from "../../../atoms/CustomButton";
import { Switch } from "../../../atoms/Switch";
import { EmptyState } from "../../../atoms/EmptyState";
import HttpService from "../../../../services/http";
import useAuth from "../../../../hooks/useAuth";
import { PlanTier, usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import FeatureUpgradePrompt from "../../../atoms/FeatureUpgradePrompt";
import { PlansModal } from "../../../modals/PlansModal";
import parse from "html-react-parser";
import { FieldLabel } from "../../../atoms/FieldLabel";

interface GoogleReviewsFormProps {
  google_places?: {
    google_place_id: string;
    review_link_enabled: boolean;
    formattedAddress: string;
    rating: number;
    displayName: string;
  };
  badges?: string[];
}

interface GoogleReviewsSettingsProps {
  data?: GoogleReviewsFormProps;
  onSubmit: (formData: GoogleReviewsFormProps) => Promise<void>;
}

interface GooglePlaceSearchResult {
  place_id: string;
  name: string;
  formatted_address: string;
  rating?: number;
  google_maps_uri?: string;
}

const StarRating: React.FC<{ rating: number; size?: number }> = ({
  rating,
  size = 12,
}) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;

  for (let i = 0; i < fullStars; i++) {
    stars.push(
      <AiFillStar key={`filled-${i}`} size={size} className="text-amber-500" />
    );
  }

  if (hasHalfStar) {
    stars.push(
      <div key="half-star" className="relative inline-block">
        <AiOutlineStar size={size} className="text-amber-500" />
        <div
          className="absolute top-0 left-0 overflow-hidden text-amber-500"
          style={{ width: `${(rating % 1) * 100}%` }}
        >
          <AiFillStar size={size} />
        </div>
      </div>
    );
  }

  const remainingStars = 5 - Math.ceil(rating);
  for (let i = 0; i < remainingStars; i++) {
    stars.push(
      <AiOutlineStar
        key={`empty-${i}`}
        size={size}
        className="text-amber-500"
      />
    );
  }

  return (
    <div className="flex items-center space-x-0.5">
      {stars}
      <span className="ml-1 text-xs text-gray-600">({rating.toFixed(1)})</span>
    </div>
  );
};

const GoogleReviewsSettings: React.FC<GoogleReviewsSettingsProps> = ({
  data,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const [selectedPlace, setSelectedPlace] =
    useState<GooglePlaceSearchResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const [reviewEnabled, setReviewEnabled] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<GooglePlaceSearchResult[]>(
    []
  );
  const [showSearchResults, setShowSearchResults] = useState(false);

  useEffect(() => {
    if (data?.google_places) {
      setReviewEnabled(data.google_places.review_link_enabled || false);
      if (
        data.google_places.displayName &&
        data.google_places.displayName.trim() !== ""
      ) {
        const reconstructedPlace: GooglePlaceSearchResult = {
          place_id: data.google_places.google_place_id || "",
          name: data.google_places.displayName,
          formatted_address: data.google_places.formattedAddress || "",
          rating: undefined,
        };
        setSelectedPlace(reconstructedPlace);
      } else {
        setSelectedPlace(null);
      }
      setHasChanges(false);
    } else {
      setReviewEnabled(false);
      setSelectedPlace(null);
      setHasChanges(false);
    }
  }, [data]);

  const searchPlaces = async (query: string) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await HttpService.get(
        `/${customer_identification}/${user.entity_identification}/${
          user.user_identification
        }/google-places/search?query=${encodeURIComponent(
          query.trim()
        )}&max_results=5`
      );

      if (response.data?.element?.results) {
        setSearchResults(response.data.element.results);
        setShowSearchResults(true);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
      setShowSearchResults(false);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery && reviewEnabled && !selectedPlace) {
        searchPlaces(searchQuery);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, reviewEnabled, selectedPlace, searchPlaces]);

  const hasGoogleReviewsAccess = permissions.isAtLeastTier(PlanTier.PREMIUM);
  const canWrite = permissions.can(Cap.LOYALTY, Level.WRITE);

  const selectPlaceFromSearch = (place: GooglePlaceSearchResult) => {
    setSelectedPlace(place);
    setSearchQuery("");
    setShowSearchResults(false);
    setSearchResults([]);
    setHasChanges(true);
  };

  const clearSelectedPlace = () => {
    setSelectedPlace(null);
    setHasChanges(true);
  };

  const handleReviewEnabledChange = async (enabled: boolean) => {
    const savedEnabled = data?.google_places?.review_link_enabled || false;

    if (enabled === savedEnabled || isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);
      setReviewEnabled(enabled);

      const process_data = {
        google_places: {
          google_place_id:
            selectedPlace?.place_id ||
            data?.google_places?.google_place_id ||
            "",
          review_link_enabled: enabled,
          formattedAddress:
            selectedPlace?.formatted_address ||
            data?.google_places?.formattedAddress ||
            "",
          rating: selectedPlace?.rating || data?.google_places?.rating || 0,
          displayName:
            selectedPlace?.name || data?.google_places?.displayName || "",
        },
      };

      await onSubmit(process_data);
      setHasChanges(false);
    } catch (error) {
      console.error("Failed to save Google Reviews toggle:", error);
      setReviewEnabled(savedEnabled);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);
    try {
      const process_data = {
        google_places: {
          google_place_id: selectedPlace?.place_id || "",
          review_link_enabled: reviewEnabled,
          formattedAddress: selectedPlace?.formatted_address || "",
          rating: selectedPlace?.rating || 0,
          displayName: selectedPlace?.name || "",
        },
      };

      await onSubmit(process_data);
      setHasChanges(false);
    } catch (error) {
      console.error("Failed to save Google Reviews settings:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!hasGoogleReviewsAccess) {
    return (
      <div className="w-full mt-6">
        <div className="flex justify-center mb-6">
          <img
            src="https://cdn.1cdn.app/application/UMD/25083018084491_google_my_business.png"
            alt="Google My Business"
            className="w-48 h-12"
          />
        </div>

        <div className="flex items-center mb-4">
          <AiFillStar className="text-gray-400 mr-2" size={18} />
          <h3 className="text-base font-semibold text-gray-900">
            {t("dashboard.features.lmc.googleReviews.title")}
          </h3>
        </div>

        <FeatureUpgradePrompt
          featureName={
            t("dashboard.features.lmc.googleReviews.premiumRequired") as string
          }
          featureDescription={
            t(
              "dashboard.features.lmc.googleReviews.premiumRequiredDescription"
            ) as string
          }
          benefits={[
            t("dashboard.features.lmc.googleReviews.benefit1") as string,
            t("dashboard.features.lmc.googleReviews.benefit2") as string,
            t("dashboard.features.lmc.googleReviews.benefit3") as string,
            t("dashboard.features.lmc.googleReviews.benefit4") as string,
          ]}
          onUpgrade={() => setShowUpgradeModal(true)}
          className="mb-5"
        />

        <PlansModal
          showPlansModal={showUpgradeModal}
          setShowPlansModal={setShowUpgradeModal}
          targetPlan={PlanTier.PREMIUM}
        />

        <div className="self-stretch h-px bg-gray-200 block mt-6" />
      </div>
    );
  }

  return (
    <div className="w-full mt-6">
      <div className="flex justify-center mb-6">
        <img
          src="https://cdn.1cdn.app/application/UMD/25083018084491_google_my_business.png"
          alt="Google My Business"
          className="w-48 h-12"
        />
      </div>

      <div className="flex items-center mb-4 gap-2">
        <AiFillStar className="text-gray-400" size={18} />
        <h3 className="text-base font-semibold text-gray-900">
          {t("dashboard.features.lmc.googleReviews.title")}
        </h3>
      </div>

      <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white border border-gray-200 rounded-md flex items-center justify-center flex-shrink-0">
              <AiFillStar size={18} className="text-gray-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-gray-900">
                {t("dashboard.features.lmc.googleReviews.emptyStateTitle")}
              </h4>
              <p className="mt-0.5 text-xs text-gray-500">
                {t("dashboard.features.lmc.googleReviews.emptyStateStat")}
              </p>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-md p-3 text-xs">
            <p className="mb-2 font-semibold text-gray-900">
              {t("dashboard.features.lmc.googleReviews.emptyTopCriteriaTitle")}
            </p>
            <ol className="flex flex-col gap-1.5 text-gray-700">
              <li className="flex items-center gap-2">
                <span className="tabular-nums text-gray-400">1.</span>
                <span>
                  {t("dashboard.features.lmc.googleReviews.emptyTopCriteria1")}
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span className="tabular-nums text-gray-400">2.</span>
                <span>
                  {t("dashboard.features.lmc.googleReviews.emptyTopCriteria2")}
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span className="tabular-nums text-gray-400">3.</span>
                <span>
                  {t("dashboard.features.lmc.googleReviews.emptyTopCriteria3")}
                </span>
              </li>
            </ol>
          </div>
        </div>
      </div>

      <form onSubmit={handleFormSubmit}>
        <div className="mb-6">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
            <div>
              <label
                htmlFor="review_link_enabled"
                className="text-gray-700 text-sm font-semibold font-figtree"
              >
                {t("dashboard.features.lmc.googleReviews.enableToggleLabel")}
              </label>
              <p className="text-gray-500 text-xs font-normal font-figtree mt-1">
                {parse(
                  t(
                    "dashboard.features.lmc.googleReviews.enableToggleDescription"
                  )
                )}
              </p>
            </div>
            <Switch
              id="review_link_enabled"
              checked={reviewEnabled}
              disabled={isSubmitting || !canWrite}
              onChange={handleReviewEnabledChange}
            />
          </div>
        </div>

        {reviewEnabled && !selectedPlace && (
          <div className="mb-4">
            <FieldLabel>
              {t("dashboard.features.lmc.googleReviews.searchBusinessLabel")}
            </FieldLabel>

            <div className="relative">
              <TextInput
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={
                  t(
                    "dashboard.features.lmc.googleReviews.searchPlaceholder"
                  ) as string
                }
                className="w-full"
                onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
              />
              {isSearching && (
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                  <BiSearch className="animate-pulse text-gray-400" size={16} />
                </div>
              )}

              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {searchResults.map((place, index) => (
                    <div
                      key={place.place_id || index}
                      onClick={() => selectPlaceFromSearch(place)}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-gray-900 text-sm">
                          {place.name}
                        </div>
                        {place.rating && (
                          <StarRating rating={place.rating} size={12} />
                        )}
                      </div>
                      <div className="text-gray-600 text-xs mt-1">
                        {place.formatted_address}
                      </div>
                      <div className="text-blue-600 text-xs mt-1">
                        {t(
                          "dashboard.features.lmc.googleReviews.clickToSelectText"
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {showSearchResults && searchResults.length === 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                  <EmptyState
                    className="py-4"
                    title={t(
                      "dashboard.features.lmc.googleReviews.noPlacesFoundText"
                    )}
                  />
                </div>
              )}
            </div>

            <p className="text-gray-500 text-xs font-normal font-figtree mt-2">
              {t("dashboard.features.lmc.googleReviews.searchHelpText")}
            </p>
          </div>
        )}

        {reviewEnabled && selectedPlace && (
          <div className="mb-6">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t("dashboard.features.lmc.googleReviews.selectedBusinessLabel")}
            </label>

            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex flex-col gap-2">
                <div className="grid grid-cols-[1fr_auto] gap-3 items-center">
                  <div className="font-medium text-gray-900 text-sm truncate">
                    {selectedPlace.name}
                  </div>
                  <div className="flex items-center flex-shrink-0">
                    {selectedPlace.rating && (
                      <StarRating rating={selectedPlace.rating} size={12} />
                    )}
                  </div>
                </div>

                <div className="text-gray-600 text-xs break-words">
                  {selectedPlace.formatted_address}
                </div>

                <div className="grid grid-cols-[1fr_auto] gap-3 items-center">
                  <div className="flex items-center text-green-600 text-xs">
                    <BiCheckCircle className="mr-1 flex-shrink-0" size={12} />
                    <span>
                      {t(
                        "dashboard.features.lmc.googleReviews.businessSelectedText"
                      )}
                    </span>
                  </div>
                  <CustomButton
                    type="button"
                    onClick={clearSelectedPlace}
                    disabled={!canWrite}
                    variant="secondary"
                    size="xs"
                    className="min-w-0 flex-shrink-0"
                    title={
                      t(
                        "dashboard.features.lmc.googleReviews.changeButtonText"
                      ) as string
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {reviewEnabled && selectedPlace && hasChanges && (
          <CustomButton
            type="submit"
            disabled={isSubmitting || !canWrite}
            variant="primary"
            className="w-full"
            title={
              isSubmitting ? "Saving..." : (t("common.saveShort") as string)
            }
          />
        )}
      </form>
    </div>
  );
};

export default React.memo(GoogleReviewsSettings);
